import { AutoCompleteContainer, CustomColorFinishOptionDivider, SelectionDetails } from "../addGlobalAttributes.styles";
import { Controller, useFormContext } from "react-hook-form";
import AutoComplete from "../../../Common/Autocomplete/Autocomplete";
import { GlobalAttributeFields } from "../forms/schema";
import {
	CustomColorsDividerText,
	CustomColorSpeciesNoteText,
	SelectCaseColor,
	SelectConstruction,
	SelectFinish,
	SelectHingeType,
	SelectProfile,
	SelectShape,
	SelectSpecies
} from "../../constants";
import { useLazyGetSpeciesDataQuery, useLazyGetStyleDataQuery } from "features/api/globalsApi.ts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { useEffect, useState } from "react";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import {
	formatFinishOption,
	sortCustomColorFinishesByDescription,
	sortGlobalByDescription
} from "components/NewOrders/utils/NewOrderUtils.tsx";
import { models } from "types/api/viewModels.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { useDialog } from "hooks/useDialog.ts";
import { Divider } from "@mui/material";

interface StepTwoProps {
	isUpdate?: boolean;
	isFixingGlobalAttributes?: boolean;
	isCustomColor: boolean;
	customQuoteGlobals?: models["GlobalsViewModel"];
}

const StepTwo = ({ isUpdate, isCustomColor, isFixingGlobalAttributes, customQuoteGlobals }: StepTwoProps) => {
	const { control, watch, clearErrors, setValue, resetField } = useFormContext<GlobalAttributeFields>();
	const dispatch = useDispatch();
	const selectedProductLine = watch("productLine");
	const selectedDoorStyle = watch("doorStyle");
	const selectedSpecies = watch("species");
	const { dialogStatus } = useDialog();
	const selectedFinish = watch("finish");
	const [updatedFinish, setUpdatedFinish] = useState<models["FinishViewModel"] | null>(null);
	const [updatedSpecies, setUpdatedSpecies] = useState<models["SpeciesViewModel"] | null>(null);
	const [updatedCaseColor, setUpdatedCaseColor] = useState<models["CaseViewModel"] | null>(null);
	const [updatedHingeType, setUpdatedHingeType] = useState<models["HingeTypeViewModel"] | null>(null);
	const [updatedConstruction, setUpdatedConstruction] = useState<models["ConstructionViewModel"] | null>(null);

	const [trigger, { data: doorStyleData, isLoading }] = useLazyGetStyleDataQuery();
	const [getSpeciesData, { data: speciesData, isFetching: isSpeciesLoading }] = useLazyGetSpeciesDataQuery();
	const updatedGlobalAttributes = useSelector((state: RootState) => state.newOrder.updatedGlobalAttributes);
	const customQuoteProductLine = customQuoteGlobals?.productLine;

	useEffect(() => {
		if (!selectedProductLine?.id && updatedGlobalAttributes?.globals?.productLine) {
			setValue("productLine", updatedGlobalAttributes?.globals?.productLine);
		}
	}, [updatedGlobalAttributes?.globals?.productLine, selectedProductLine?.id, setValue]);

	useEffect(() => {
		if (!selectedDoorStyle?.id && updatedGlobalAttributes?.globals?.style) {
			setValue("doorStyle", updatedGlobalAttributes?.globals?.style);
		}
	}, [updatedGlobalAttributes?.globals?.style, selectedDoorStyle?.id, setValue]);

	useEffect(() => {
		if (selectedDoorStyle?.id && selectedProductLine?.id) {
			trigger({
				productLineId: selectedProductLine?.id,
				styleId: selectedDoorStyle?.id
			});
		}
	}, [
		selectedDoorStyle?.id,
		dialogStatus,
		isCustomColor,
		isFixingGlobalAttributes,
		trigger,
		selectedProductLine?.id
	]);

	useEffect(() => {
		if (selectedSpecies?.id && selectedDoorStyle?.id && selectedProductLine?.id) {
			getSpeciesData({
				productLineId: selectedProductLine?.id,
				styleId: selectedDoorStyle?.id,
				speciesId: selectedSpecies?.id,
				customFinishes: dialogStatus == DialogTracking.CUSTOM_COLOR_ENTRY || isCustomColor
			});
		}
	}, [
		selectedSpecies?.id,
		dialogStatus,
		isCustomColor,
		selectedDoorStyle?.id,
		getSpeciesData,
		selectedProductLine?.id
	]);

	useEffect(() => {
		if (!isUpdate) {
			return;
		}
		const globals = customQuoteGlobals ?? updatedGlobalAttributes?.globals;
		const shapeValue = doorStyleData?.shapes?.find((shape) => shape.id === globals?.shape?.id);

		const speciesValue = doorStyleData?.species?.find(
			(species) => species.id === (updatedSpecies ?? globals?.species)?.id
		);

		const areFinishesEqual = (
			finish1: models["FinishViewModel"] | null | undefined,
			finish2: models["FinishViewModel"] | null | undefined
		): boolean => {
			if (!finish1 || !finish2) return false;

			const customColorsEqual =
				(!finish1.customColor && !finish2.customColor) ||
				(finish1.customColor?.vendorCode === finish2.customColor?.vendorCode &&
					finish1.customColor?.vendorDescription === finish2.customColor?.vendorDescription &&
					finish1.customColor?.colorCode === finish2.customColor?.colorCode &&
					finish1.customColor?.colorDescription === finish2.customColor?.colorDescription);

			return (
				finish1.id === finish2.id &&
				finish1.code === finish2.code &&
				finish1.description === finish2.description &&
				finish1.customColorSampleOrderNumber === finish2.customColorSampleOrderNumber &&
				customColorsEqual
			);
		};

		const finishValue = speciesData?.finishes?.find((finish) =>
			areFinishesEqual(finish, updatedFinish ?? globals?.finish)
		);

		const cabinetProfileValues = doorStyleData?.cabinetProfiles?.find(
			(profile) => profile.id === globals?.profile?.id
		);

		const hingeTypeValues = doorStyleData?.hingeTypes?.find(
			(hingeType) => hingeType.id === (updatedHingeType ?? globals?.hingeType)?.id
		);

		const caseColorValues = doorStyleData?.cases?.find(
			(caseColor) => caseColor.id === (updatedCaseColor ?? globals?.case)?.id
		);

		const constructionValues = doorStyleData?.constructions?.find(
			(construction) => construction.id === (updatedConstruction ?? globals?.construction)?.id
		);

		if (shapeValue) {
			setValue("shape", shapeValue);
		}

		if (speciesValue) {
			setValue("species", speciesValue);
		}

		if (finishValue) {
			setValue("finish", finishValue);
		}

		if (cabinetProfileValues) {
			setValue("profile", cabinetProfileValues);
		}

		if (hingeTypeValues) {
			setValue("hinge", hingeTypeValues);
		}

		if (caseColorValues) {
			setValue("caseColor", caseColorValues);
		}

		if (constructionValues) {
			setValue("construction", constructionValues);
		}
	}, [
		doorStyleData?.shapes,
		doorStyleData?.species,
		doorStyleData?.hingeTypes,
		doorStyleData?.cabinetProfiles,
		doorStyleData?.cases,
		doorStyleData?.constructions,
		speciesData?.finishes,
		isUpdate,
		setValue,
		updatedGlobalAttributes?.globals,
		updatedGlobalAttributes?.globals?.finish?.id,
		updatedGlobalAttributes?.globals?.shape?.id,
		updatedGlobalAttributes?.globals?.species?.id,
		updatedGlobalAttributes?.globals?.profile?.id,
		updatedGlobalAttributes?.globals?.hingeType?.id,
		updatedGlobalAttributes?.globals?.case?.id,
		updatedGlobalAttributes?.globals?.construction?.id,
		customQuoteGlobals,
		updatedFinish,
		updatedSpecies,
		updatedCaseColor,
		updatedHingeType,
		updatedConstruction
	]);

	const shapesOptions = doorStyleData?.shapes ?? [];
	const cabinetProfileOptions = doorStyleData?.cabinetProfiles ?? [];
	const hingeOptions = doorStyleData?.hingeTypes ?? [];
	const caseColorOptions = doorStyleData?.cases ?? [];
	const constructionOptions = doorStyleData?.constructions ?? [];
	const speciesOptions = doorStyleData?.species ?? [];

	useEffect(() => {
		if (
			dialogStatus === DialogTracking.CUSTOM_COLOR_ENTRY &&
			selectedSpecies?.finishes?.length === 1 &&
			selectedSpecies?.finishes[0]
		) {
			setValue("finish", selectedSpecies?.finishes[0]);
		}
	}, [selectedSpecies, setValue, dialogStatus]);

	const customColorEligibleSpecies = (doorStyleData?.species ?? []).filter((sp) => sp.customColorEligible);

	return (
		<AutoCompleteContainer>
			<SelectionDetails data-testid="global-attribute-modal-selection">
				{`${isUpdate ? (customQuoteProductLine?.description ?? updatedGlobalAttributes?.globals?.productLine?.description) : selectedProductLine?.description}, ${selectedDoorStyle?.description}`}
			</SelectionDetails>
			{shapesOptions.length !== 0 && (
				<Controller
					name="shape"
					control={control}
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							required
							isLoading={isLoading}
							value={value}
							disableClearable
							label={SelectShape}
							getOptionLabel={(option) => option?.description}
							onChange={(_event, value) => {
								if (isUpdate && !customQuoteGlobals) {
									const globalAttributesToBeUpdated: models["GlobalsViewModel"] = {
										...updatedGlobalAttributes?.globals,
										shape: {
											code: value.code,
											description: value.description,
											id: value.id
										}
									};
									dispatch(
										newOrderActions.updateGlobalAttributes({
											...updatedGlobalAttributes,
											globals: globalAttributesToBeUpdated
										})
									);
								}
								clearErrors("shape");
								onChange(value);
							}}
							options={sortGlobalByDescription(shapesOptions)}
							dataTestId="select-shape"
							isError={!!error}
							errorText={error?.message}
						/>
					)}
				/>
			)}
			{cabinetProfileOptions.length !== 0 && (
				<Controller
					name="profile"
					control={control}
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							required
							isLoading={isLoading}
							value={value}
							disableClearable
							label={SelectProfile}
							getOptionLabel={(option) => option?.description}
							onChange={(_event, value) => {
								if (isUpdate && !customQuoteGlobals) {
									const globalAttributesToBeUpdated: models["GlobalsViewModel"] = {
										...updatedGlobalAttributes?.globals,
										profile: {
											code: value?.code,
											description: value?.description,
											id: value?.id
										}
									};
									dispatch(
										newOrderActions.updateGlobalAttributes({
											...updatedGlobalAttributes,
											globals: globalAttributesToBeUpdated
										})
									);
								}
								clearErrors("profile");
								onChange(value);
							}}
							options={sortGlobalByDescription(cabinetProfileOptions)}
							dataTestId="select-cabinet-profile"
							isError={!!error}
							errorText={error?.message}
						/>
					)}
				/>
			)}
			{hingeOptions.length !== 0 && (
				<Controller
					name="hinge"
					control={control}
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							required
							isLoading={isLoading}
							value={value}
							disableClearable
							label={SelectHingeType}
							getOptionLabel={(option) => option?.description}
							onChange={(_event, value) => {
								if (isUpdate && !customQuoteGlobals) {
									const globalAttributesToBeUpdated: models["GlobalsViewModel"] = {
										...updatedGlobalAttributes?.globals,
										hingeType: {
											code: value?.code,
											description: value?.description,
											id: value?.id
										}
									};
									dispatch(
										newOrderActions.updateGlobalAttributes({
											...updatedGlobalAttributes,
											globals: globalAttributesToBeUpdated
										})
									);
								}
								setUpdatedHingeType(value);
								clearErrors("hinge");
								onChange(value);
							}}
							options={sortGlobalByDescription(hingeOptions)}
							dataTestId="select-hinge"
							isError={!!error}
							errorText={error?.message}
						/>
					)}
				/>
			)}
			{caseColorOptions.length !== 0 && (
				<Controller
					name="caseColor"
					control={control}
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							required
							isLoading={isLoading}
							value={value}
							disableClearable
							label={SelectCaseColor}
							getOptionLabel={(option) => option?.description}
							onChange={(_event, value) => {
								if (isUpdate && !customQuoteGlobals) {
									const globalAttributesToBeUpdated: models["GlobalsViewModel"] = {
										...updatedGlobalAttributes?.globals,
										case: {
											code: value?.code,
											description: value?.description,
											id: value?.id
										}
									};
									dispatch(
										newOrderActions.updateGlobalAttributes({
											...updatedGlobalAttributes,
											globals: globalAttributesToBeUpdated
										})
									);
								}
								setUpdatedCaseColor(value);
								clearErrors("caseColor");
								onChange(value);
							}}
							options={sortGlobalByDescription(caseColorOptions)}
							dataTestId="select-case-color"
							isError={!!error}
							errorText={error?.message}
						/>
					)}
				/>
			)}
			{constructionOptions.length !== 0 && (
				<Controller
					name="construction"
					control={control}
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							required
							isLoading={isLoading}
							value={value}
							disableClearable
							label={SelectConstruction}
							getOptionLabel={(option) => option?.description}
							onChange={(_event, value) => {
								if (isUpdate && !customQuoteGlobals) {
									const globalAttributesToBeUpdated: models["GlobalsViewModel"] = {
										...updatedGlobalAttributes?.globals,
										construction: {
											code: value?.code,
											description: value?.description,
											id: value?.id
										}
									};
									dispatch(
										newOrderActions.updateGlobalAttributes({
											...updatedGlobalAttributes,
											globals: globalAttributesToBeUpdated
										})
									);
								}
								setUpdatedConstruction(value);
								clearErrors("construction");
								onChange(value);
							}}
							options={sortGlobalByDescription(constructionOptions)}
							dataTestId="select-construction"
							isError={!!error}
							errorText={error?.message}
						/>
					)}
				/>
			)}
			{speciesOptions.length !== 0 && (
				<Controller
					name="species"
					control={control}
					render={({ field: { onChange, value = null }, fieldState: { error } }) => (
						<AutoComplete
							required
							isLoading={isLoading}
							value={value}
							disableClearable
							label={SelectSpecies}
							getOptionLabel={(option) => option?.description}
							onChange={(_event, value) => {
								if (isUpdate && !customQuoteGlobals) {
									const globalAttributesToBeUpdated: models["GlobalsViewModel"] = {
										...updatedGlobalAttributes?.globals,
										species: {
											code: value.code,
											description: value.description,
											id: value.id
										},
										finish: {
											...updatedGlobalAttributes?.globals?.finish,
											code: null,
											description: null,
											id: null,
											customColor: updatedGlobalAttributes?.globals?.finish?.customColor
										}
									};
									dispatch(
										newOrderActions.updateGlobalAttributes({
											...updatedGlobalAttributes,
											globals: globalAttributesToBeUpdated
										})
									);
								}
								setUpdatedSpecies(value); // add this line
								clearErrors("species");
								resetField("finish", { defaultValue: null as any });
								onChange(value);
							}}
							options={
								customColorEligibleSpecies.length > 0 && isCustomColor
									? sortGlobalByDescription(customColorEligibleSpecies)
									: sortGlobalByDescription(speciesOptions)
							}
							dataTestId="select-wood-species"
							isError={!!error}
							errorText={error?.message}
						/>
					)}
				/>
			)}
			<Controller
				name="finish"
				control={control}
				render={({ field: { onChange, value = null }, fieldState: { error } }) => (
					<AutoComplete
						required
						value={value}
						isLoading={isSpeciesLoading}
						disabled={!selectedSpecies || isSpeciesLoading}
						disableClearable
						label={SelectFinish}
						onChange={(_event, value) => {
							if (isUpdate && !customQuoteGlobals) {
								const globalAttributesToBeUpdated: models["GlobalsViewModel"] = {
									...updatedGlobalAttributes?.globals,
									finish: {
										...updatedGlobalAttributes?.globals?.finish,
										code: value.code,
										description: value.description,
										id: value.id,
										customColorSampleOrderNumber: value.customColorSampleOrderNumber,
										customColor: value?.customColor
									}
								};
								dispatch(
									newOrderActions.updateGlobalAttributes({
										...updatedGlobalAttributes,
										globals: globalAttributesToBeUpdated
									})
								);
								setUpdatedFinish(value);
							}
							clearErrors("finish");
							onChange(value);
						}}
						options={sortCustomColorFinishesByDescription(isCustomColor, speciesData?.finishes ?? [])}
						getOptionLabel={(option) => formatFinishOption(option)}
						getOptionDisabled={(option) => option.type === "divider"}
						renderOption={(props, option) =>
							option.type === "divider" ? (
								<CustomColorFinishOptionDivider>
									<div>{CustomColorsDividerText}</div>
									<Divider variant="fullWidth" />
								</CustomColorFinishOptionDivider>
							) : (
								<li {...props}>{formatFinishOption(option)}</li>
							)
						}
						dataTestId="select-finish"
						isError={!!error}
						errorText={error?.message}
					/>
				)}
			/>
			{selectedFinish?.customColorSampleOrderNumber && <div>{CustomColorSpeciesNoteText}</div>}
		</AutoCompleteContainer>
	);
};

export default StepTwo;
